/** SUNTRONIX ONE — canonical, reusable lifecycle presentation component.
 *
 *  One component, five variants — driven by the same source data
 *  (see /app/frontend/src/lib/lifecycle.js).  Use this wherever the full
 *  seven-stage lifecycle is shown, so every surface stays in sync.
 *
 *  Variants:
 *    "full"     — horizontal chain with hairline separators (login brand rail)
 *    "compact"  — inline "Procure · Deploy · … · Recycle" tagline signature
 *    "timeline" — vertical timeline used by dashboards and Asset 360
 *    "circular" — concentric arc suggesting REUSE → RECYCLE → PROCURE loop
 *    "mobile"   — 2-row wrap for narrow screens
 *
 *  Motion is CSS-only and disabled under prefers-reduced-motion.
 */
import { LIFECYCLE_STAGES, LIFECYCLE_LABELS } from "@/lib/lifecycle";

export default function LifecycleSignature({
  variant = "full",
  showTaglines = false,
  className = "",
  animated = false,
  "data-testid": testId,
}) {
  if (variant === "compact") {
    return (
      <span
        data-testid={testId || "lifecycle-signature-compact"}
        className={
          "inline text-[11px] uppercase tracking-[0.18em] text-[color:var(--st-text-muted)] " + className
        }
      >
        {LIFECYCLE_LABELS.join(" · ")}
      </span>
    );
  }

  if (variant === "mobile") {
    // Split 4/3 for legibility on narrow screens per lifecycle spec §5
    const top = LIFECYCLE_LABELS.slice(0, 3);
    const bottom = LIFECYCLE_LABELS.slice(3);
    return (
      <div
        data-testid={testId || "lifecycle-signature-mobile"}
        className={"space-y-2 text-[10px] uppercase tracking-[0.18em] text-[color:var(--st-text-subtle)] " + className}
      >
        <Row items={top} />
        <Row items={bottom} />
      </div>
    );
  }

  if (variant === "timeline") {
    return (
      <ol
        data-testid={testId || "lifecycle-signature-timeline"}
        className={"relative pl-6 space-y-4 " + className}
      >
        <span className="absolute left-[7px] top-1 bottom-1 w-px bg-[color:var(--st-border-strong)]" aria-hidden="true" />
        {LIFECYCLE_STAGES.map((s) => (
          <li key={s.key} className="relative">
            <span className="absolute -left-6 top-1 h-[15px] w-[15px] rounded-full border border-[color:var(--st-border-strong)] bg-[color:var(--st-bg)]" aria-hidden="true" />
            <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[color:var(--st-text)]">{s.label}</div>
            {showTaglines && (
              <div className="text-[12px] text-[color:var(--st-text-muted)] mt-0.5">{s.tagline}</div>
            )}
          </li>
        ))}
      </ol>
    );
  }

  if (variant === "circular") {
    // Simple circular list used on marketing / executive surfaces.
    return (
      <div
        data-testid={testId || "lifecycle-signature-circular"}
        className={"relative aspect-square max-w-[360px] mx-auto " + className}
        role="img"
        aria-label={"Circular technology lifecycle: " + LIFECYCLE_LABELS.join(", ")}
      >
        <div className="absolute inset-0 rounded-full border border-[color:var(--st-border-strong)]" />
        <div className="absolute inset-6 rounded-full border border-dashed border-[color:var(--st-border)]" />
        {LIFECYCLE_STAGES.map((s, i) => {
          const angle = (i / LIFECYCLE_STAGES.length) * 360 - 90;
          const rad = (angle * Math.PI) / 180;
          const r = 46; // %
          const x = 50 + r * Math.cos(rad);
          const y = 50 + r * Math.sin(rad);
          return (
            <span
              key={s.key}
              className="absolute -translate-x-1/2 -translate-y-1/2 text-[10px] uppercase tracking-[0.18em] font-semibold text-[color:var(--st-text)] text-center whitespace-nowrap"
              style={{ left: `${x}%`, top: `${y}%` }}
            >
              {s.label}
              {i === LIFECYCLE_STAGES.length - 1 && (
                <span className="block text-[9px] font-normal tracking-[0.14em] text-[color:var(--st-text-muted)] mt-0.5">closes to Procure</span>
              )}
            </span>
          );
        })}
        <div className="absolute inset-0 grid place-items-center text-center px-8">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[color:var(--st-text-muted)]">Technology</div>
            <div className="font-heading text-[18px] font-medium tracking-[-0.01em] mt-1">Lifecycle</div>
            <div className="text-[10px] text-[color:var(--st-text-subtle)] mt-2 leading-tight max-w-[16ch] mx-auto">
              Reuse first. Recycle when reuse is no longer appropriate.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default: "full" horizontal chain
  return (
    <div
      data-testid={testId || "lifecycle-signature-full"}
      className={"flex flex-wrap gap-x-6 gap-y-3 text-[11px] tracking-[0.18em] uppercase text-[color:var(--st-text-subtle)] " + className}
      role="list"
      aria-label={"Technology lifecycle: " + LIFECYCLE_LABELS.join(", ")}
    >
      {LIFECYCLE_STAGES.map((s, i) => (
        <span
          key={s.key}
          role="listitem"
          className={"flex items-center gap-3 " + (animated ? "st-rail-label" : "")}
          style={animated ? { "--i": i } : undefined}
        >
          <span className="h-px w-6 bg-[color:var(--st-border-strong)]" />{s.label}
        </span>
      ))}
    </div>
  );
}

function Row({ items }) {
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1">
      {items.map((s) => (
        <span key={s} className="flex items-center gap-2">
          <span className="h-px w-4 bg-[color:var(--st-border-strong)]" />{s}
        </span>
      ))}
    </div>
  );
}
